import { onFind } from "@elements/init-modules-in-scope";
import { findIn, findAllIn, on } from "@elements/dom-utils";
import { getPrefixedDataSet } from "@elements/data-set-utils";

const selectors = {
    wrapper: '.js-ally-scroll__wrapper',
    content: '.js-ally-scroll__content',
    prev: '.js-ally-scroll__prev',
    next: '.js-ally-scroll__next',
    arrows: '.js-ally-scroll__arrows',
    template: '.js-ally-scroll__controls',
    item: '.js-ally-scroll__item',
};

const eventsBound = new WeakSet();

export function init() {
    onFind(selectors.wrapper, (scrollWrapper) => {
        const scrollContent = findIn(selectors.content, scrollWrapper);
        if (!scrollContent) return;

        setupScrollWrapper(scrollWrapper, scrollContent);
        handleResize(scrollWrapper, scrollContent);
        observeSize(scrollWrapper, scrollContent);
    });
}

function setupScrollWrapper(scrollWrapper, scrollContent) {
    updateNavigation(scrollWrapper, scrollContent);

    const hasOverflow = isScrollable(scrollContent);
    const prevButton = findIn(selectors.prev, scrollWrapper);
    const nextButton = findIn(selectors.next, scrollWrapper);
    if (hasOverflow && prevButton && nextButton) {
        setupScrollEvents(scrollWrapper, scrollContent);
        updateScrollButtons(scrollContent, prevButton, nextButton);
    }
}

function isScrollable(element) {
    return element.scrollWidth > element.clientWidth;
}

function getScrollOffset(scrollWrapper, scrollContent) {
    const data = getPrefixedDataSet('ally-scroll', scrollWrapper);
    const rawOffset = data?.offset;

    if (typeof rawOffset === 'string') {
        if (rawOffset.toLowerCase() === 'item-width') {
            const items = findAllIn(selectors.item, scrollWrapper);
            let minWidth = Infinity;
            items.forEach((el) => {
                const w = el ? el.clientWidth : 0;
                if (w > 0 && w < minWidth) minWidth = w;
            });
            return Number.isFinite(minWidth) ? minWidth : 120;
        }
        const num = Number(rawOffset);
        if (!Number.isNaN(num) && num > 0) return num;
    } else if (typeof rawOffset === 'number' && rawOffset > 0) {
        return rawOffset;
    }

    return 120;
}

function addNavigationFromTemplate(scrollWrapper) {
    const template = findIn(selectors.template, scrollWrapper);
    if (!template || !template.content) return false;

    const clone = template.content.cloneNode(true);
    scrollWrapper.appendChild(clone);
    return true;
}

function removeNavigation(scrollWrapper) {
    const arrows = findIn(selectors.arrows, scrollWrapper);
    if (arrows) arrows.remove();
}

function updateNavigation(scrollWrapper, scrollContent) {
    removeNavigation(scrollWrapper);
    if (isScrollable(scrollContent)) {
        addNavigationFromTemplate(scrollWrapper);
    }
}

function toggleButtonState(button, isEnabled) {
    if (!button) return;
    button.toggleAttribute('disabled', !isEnabled);
}

function setupScrollEvents(scrollWrapper, scrollContent) {
    if (eventsBound.has(scrollWrapper)) {
        return;
    }
    eventsBound.add(scrollWrapper);

    on('click', (event) => {
        const target = event.target;
        const isPrev = !!target.closest(selectors.prev);
        const isNext = !!target.closest(selectors.next);
        if (!isPrev && !isNext) return;

        event.preventDefault();

        const baseScrollOffset = getScrollOffset(scrollWrapper, scrollContent);
        const maxScrollLeft = scrollContent.scrollWidth - scrollContent.clientWidth;
        const current = scrollContent.scrollLeft;

        const remaining = isPrev ? Math.max(0, current) : Math.max(0, maxScrollLeft - current);
        const allowed = Math.min(baseScrollOffset, remaining);
        const delta = isPrev ? -allowed : allowed;

        scrollContent.scrollBy({ left: delta, behavior: "smooth" });
    }, scrollWrapper);

    on('scroll', () => {
        const prevButton = findIn(selectors.prev, scrollWrapper);
        const nextButton = findIn(selectors.next, scrollWrapper);
        updateScrollButtons(scrollContent, prevButton, nextButton);
    }, scrollContent);
}

function updateScrollButtons(
    scrollContent,
    prevButton,
    nextButton
) {
    const maxScrollLeft = scrollContent.scrollWidth - scrollContent.clientWidth;
    const scrollLeft = scrollContent.scrollLeft;

    const tolerance = 1;

    toggleButtonState(prevButton, scrollLeft > tolerance);
    toggleButtonState(nextButton, scrollLeft < maxScrollLeft - tolerance);
}

function handleResize(scrollWrapper, scrollContent) {
    let resizeTimeout;
    on('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(() => {
            updateNavigation(scrollWrapper, scrollContent);
            const prevButton = findIn(selectors.prev, scrollWrapper);
            const nextButton = findIn(selectors.next, scrollWrapper);
            if (prevButton && nextButton) {
                setupScrollEvents(scrollWrapper, scrollContent);
                updateScrollButtons(scrollContent, prevButton, nextButton);
            }
        }, 100);
    }, window);
}

function observeSize(scrollWrapper, scrollContent) {
    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
        updateNavigation(scrollWrapper, scrollContent);

        const prevButton = findIn(selectors.prev, scrollWrapper);
        const nextButton = findIn(selectors.next, scrollWrapper);

        if (prevButton && nextButton) {
            setupScrollEvents(scrollWrapper, scrollContent);
            updateScrollButtons(scrollContent, prevButton, nextButton);
        }
    });

    ro.observe(scrollWrapper);
    ro.observe(scrollContent);
}